import { useEffect, useState } from "react";
import { Button, Card, Flex, LinearProgressIndicator, Text } from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import * as styles from "../styles_vanilla.css";
import { ExtractionStatus } from "./useDocumentExtractionPolling";

interface ProfileCreationModalFooterProps {
  isUploading: boolean;
  onUploadWithAI: () => void;
  onDocumentUploadSuccess?: boolean;
  extractionStatus?: ExtractionStatus;
}

export const ProfileCreationModalFooter = ({
  isUploading,
  onUploadWithAI,
  onDocumentUploadSuccess,
  extractionStatus,
}: ProfileCreationModalFooterProps) => {
  const [progress, setProgress] = useState(0);
  const [isExtracting, setIsExtracting] = useState(false);
  const extractionFailed =
    extractionStatus === ExtractionStatus.Failed ||
    extractionStatus === ExtractionStatus.UnsupportedDocType;

  useEffect(() => {
    if (!isExtracting || extractionFailed) return;

    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 2;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isExtracting, extractionFailed]);

  const displayProgress = extractionStatus === ExtractionStatus.Succeeded ? 100 : progress;

  return (
    <>
      {isExtracting && !extractionFailed ? (
        <Card className={styles.progressIndicatorContainer}>
          <Flex flexDirection="column">
            <Text variant="regular-md" className={styles.textColor}>
              {onDocumentUploadSuccess
                ? `Extracting data from your document...`
                : `Uploading document...`}
            </Text>
            <Text variant="regular-sm">This can take up to a minute.</Text>
          </Flex>
          <LinearProgressIndicator
            className={styles.progressIndicatorBar}
            progressPercentage={displayProgress}
          />
        </Card>
      ) : (
        <Flex flexDirection="column" gap={Sizes.MD} className={styles.footerContainer}>
          <Button
            variant="primary"
            onClick={() => {
              setProgress(0);
              setIsExtracting(true);
              onUploadWithAI();
            }}
            isLoading={isUploading}
            disabled={isUploading}
            isFullWidth
          >
            Upload and extract data with AI
          </Button>

          <Text variant="regular-sm" className={styles.footerDescriptionText}>
            If identification documents (e.g., Driver&apos;s License, Passport, Certificate of
            Formation, Articles of Incorporation) are included, we will extract key data for
            profile creation or assignment.
          </Text>
        </Flex>
      )}
    </>
  );
};
